import { useMutation } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const SELECT_PROPOSED_ANSWER_REVISION_MUTATION = gql`
    mutation SelectProposedAnswerRevision($messageId: UUID!, $revisionId: UUID!) {
        messageSelectProposedAnswerRevision(messageId: $messageId, revisionId: $revisionId)
    }
`;

export interface ProposedAnswerRevision {
    id: string;
    createdAt: string;
    revisionNumber: number;
    proposedAnswer: string;
    source: string;
    parentRevisionId?: string | null | undefined;
    languageCode?: string | null | undefined;
}

/** German default labels for revision source values. Consumed through
 *  getSourceLabel, which resolves `revisionHistory.source.<VALUE>` via i18n
 *  first — these are the defaultValue fallbacks (and the de.json content). */
export const REVISION_SOURCE_LABELS: Record<string, string> = {
    GENERATION: "Generiert",
    ASSISTANT_REWRITE: "KI-Umschreibung",
    MANUAL_REVERT: "Wiederherstellung",
    MANUAL_EDIT: "Manuell bearbeitet",
    PARAGRAPH_DISMISSAL: "Absatz entfernt",
    FACT_SET_REUSE: "Fakt geändert",
    TRANSLATION: "Übersetzt",
};

/** Humane fallback for unknown source values: "SOME_NEW_SOURCE" -> "Some new source". */
function humanizeSource(source: string): string {
    const words = source.replace(/_/g, " ").trim().toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

interface UseProposedAnswerRevisionsOptions {
    messageId: string | null | undefined;
    /** The message's current pointer (Message.currentRevisionId from the query/
     * subscription). Null means "at the tip" -- no revisions yet, or the
     * pointer was never moved away from the latest one. */
    currentRevisionId: string | null | undefined;
    revisions: ProposedAnswerRevision[] | null | undefined;
    onSelected?: () => void;
}

interface UseProposedAnswerRevisionsReturn {
    /** Revisions sorted newest-first. */
    sortedRevisions: ProposedAnswerRevision[];
    /** Pass-through of the currentRevisionId input, for binding the dropdown's value. */
    currentRevisionId: string | null | undefined;
    /** The revision object the pointer is on (null if there are no revisions, or
     * the pointer is at the tip and that revision isn't in `revisions`). */
    viewingRevision: ProposedAnswerRevision | null;
    /** True when the pointer is parked behind the tip (not on the newest revision). */
    isViewingOlderVersion: boolean;
    /** Whether the select-revision mutation is in flight. */
    isSelecting: boolean;
    /** Move the pointer to a revision (GitLab #424). Resolves once the server
     * pointer move + follow-up refetch have completed, so callers can couple the
     * loading spinner and the text swap to the same moment. */
    selectRevision: (revisionId: string) => Promise<void>;
    /** Human-readable label for a revision source value. */
    getSourceLabel: (source: string) => string;
}

export function useProposedAnswerRevisions({
    messageId,
    currentRevisionId,
    revisions,
    onSelected,
}: UseProposedAnswerRevisionsOptions): UseProposedAnswerRevisionsReturn {
    const { t } = useTranslation();
    const [selectMutation, { loading: isSelecting }] = useMutation(
        SELECT_PROPOSED_ANSWER_REVISION_MUTATION,
        {
            onError: (error) => {
                toast.error(`Fehler: ${error.message}`);
            },
        },
    );

    const sortedRevisions = revisions
        ? [...revisions].sort((a, b) => b.revisionNumber - a.revisionNumber)
        : [];

    const viewingRevision = currentRevisionId
        ? (sortedRevisions.find((r) => r.id === currentRevisionId) ?? null)
        : null;
    // The tip is always sortedRevisions[0] (newest-first). Behind-the-tip means
    // the pointer resolved to a real revision that isn't that one.
    const isViewingOlderVersion =
        viewingRevision !== null && sortedRevisions[0]?.id !== viewingRevision.id;

    const selectRevision = (revisionId: string): Promise<void> => {
        if (!messageId || revisionId === currentRevisionId) return Promise.resolve();
        return selectMutation({ variables: { messageId, revisionId } }).then(() => {
            onSelected?.();
        });
    };

    const getSourceLabel = (source: string): string =>
        t(`revisionHistory.source.${source}`, {
            defaultValue: REVISION_SOURCE_LABELS[source] ?? humanizeSource(source),
        });

    return {
        sortedRevisions,
        currentRevisionId,
        viewingRevision,
        isViewingOlderVersion,
        isSelecting,
        selectRevision,
        getSourceLabel,
    };
}
